import { useState } from 'react';
import axios from 'axios';

/**
 * Inline email subscribe box for public channel pages.
 * Props:
 *   - channelSlug (optional): associate subscription with a specific channel
 *   - categoryName (optional): category name for context
 *   - variant: 'channel' (default) or 'general' — controls messaging
 */
function EmailSubscribeBox({ channelSlug, categoryName, variant = 'channel' }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | already | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setStatus('loading');
    setErrorMsg('');

    try {
      await axios.post('/api/public/subscribe', {
        email: email.trim(),
        channelSlug: channelSlug || undefined,
        categoryName: categoryName || undefined
      });
      setStatus('success');
      setEmail('');
    } catch (err) {
      if (err.response?.status === 409) {
        setStatus('already');
      } else if (err.response?.status === 429) {
        setStatus('error');
        setErrorMsg('Too many requests. Please try again later.');
      } else {
        setStatus('error');
        setErrorMsg(err.response?.data?.error || 'Something went wrong. Please try again.');
      }
    }
  };

  const isGeneral = variant === 'general';

  return (
    <div className="email-subscribe-box">
      <div className="email-subscribe-icon" aria-hidden="true">📬</div>

      {status === 'success' ? (
        <div className="email-subscribe-success">
          <span className="email-subscribe-check">✓</span> You're in! Check your inbox next week.
        </div>
      ) : status === 'already' ? (
        <div className="email-subscribe-success email-subscribe-already">
          You're already subscribed!
        </div>
      ) : (
        <>
          <h3 className="email-subscribe-title">
            {isGeneral ? 'Stay connected' : 'Get posts like these in your inbox'}
          </h3>
          {isGeneral && (
            <p className="email-subscribe-desc">
              Get one thoughtful discussion per week — no account needed.
            </p>
          )}

          <form className="email-subscribe-form" onSubmit={handleSubmit}>
            <input
              type="email"
              className="email-subscribe-input"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={status === 'loading'}
              aria-label="Email address"
            />
            <button
              type="submit"
              className="email-subscribe-btn"
              disabled={status === 'loading' || !email.trim()}
            >
              {status === 'loading' ? '...' : 'Subscribe'}
            </button>
          </form>

          {errorMsg && <p className="email-subscribe-error">{errorMsg}</p>}

          <p className="email-subscribe-disclaimer">
            {isGeneral
              ? "We'll never spam you. Unsubscribe anytime."
              : 'One email per week. No spam. Unsubscribe anytime.'}
          </p>
        </>
      )}
    </div>
  );
}

export default EmailSubscribeBox;
